import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const USAGE_STORAGE_KEY = 'cutcalc-usage';
const LICENSE_STORAGE_KEY = 'cutcalc-license';
const DEV_PRO_STORAGE_KEY = 'cutcalc-dev-pro';

// Your Lemon Squeezy store/product URL (update this when you create your product)
export const CHECKOUT_URL = 'https://cutcalc.lemonsqueezy.com/checkout/buy/72a103aa-f46b-4511-8d9a-d5edfcaee15a';

// Free tier limits
export const FREE_TIER_LIMITS = {
  ALGORITHMS: ['bottom-left'], // Only 'Quick' algorithm
};

// Feature flags
export const FEATURES = {
  ALL_ALGORITHMS: 'all_algorithms',
  MODEL_IMPORT: 'model_import',
  CLEAN_EXPORT: 'clean_export',
  PROJECT_SAVE: 'project_save',
};

// API endpoints
// Use local dev server (port 3001) when running on localhost, otherwise use relative URL for Vercel
const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
  window.location.port === '3000';
const API_BASE = isLocalDev ? 'http://localhost:3001' : '';
const RATE_LIMIT_API = `${API_BASE}/api/check-import-limit`;
const LICENSE_API = `${API_BASE}/api/license`;

/**
 * Load usage data from localStorage
 * Note: Most rate limiting is now server-side, this is just for UI hints
 */
const loadUsageData = () => {
  return {};
};

/**
 * Load license data from localStorage
 */
const loadLicenseData = () => {
  try {
    const saved = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load license data:', e);
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  // License state
  const [license, setLicense] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [licenseError, setLicenseError] = useState(null);

  // Usage tracking
  const [usage, setUsage] = useState(loadUsageData);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');

  // License key modal state
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  /**
   * Validate a license key with Lemon Squeezy (via server-side proxy)
   */
  const validateLicense = useCallback(async (licenseKey) => {
    try {
      const response = await fetch(LICENSE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'validate',
          license_key: licenseKey,
        }),
      });

      const data = await response.json();

      if (data.valid) {
        return {
          valid: true,
          license: data.license_key,
          meta: data.meta,
        };
      } else {
        return {
          valid: false,
          error: data.error || 'Invalid license key',
        };
      }
    } catch (error) {
      console.error('License validation error:', error);
      return {
        valid: false,
        error: 'Failed to validate license. Please check your internet connection.',
      };
    }
  }, []);

  /**
   * Activate a license key (via server-side proxy)
   */
  const activateLicense = useCallback(async (licenseKey) => {
    setLicenseError(null);

    // Generate a unique instance ID for this device
    const instanceId = localStorage.getItem('cutcalc-instance-id') ||
      `cutcalc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('cutcalc-instance-id', instanceId);

    try {
      const response = await fetch(LICENSE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'activate',
          license_key: licenseKey,
          instance_name: instanceId,
        }),
      });

      const data = await response.json();

      if (data.activated || data.valid) {
        const licenseData = {
          key: licenseKey,
          instanceId: data.instance?.id || instanceId,
          activatedAt: new Date().toISOString(),
          customerEmail: data.meta?.customer_email,
          productName: data.meta?.product_name,
          variant: data.meta?.variant_name,
        };

        setLicense(licenseData);
        setIsPro(true);
        localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(licenseData));

        return { success: true, license: licenseData };
      } else {
        const errorMsg = data.error || 'Failed to activate license';
        setLicenseError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('License activation error:', error);
      const errorMsg = 'Failed to activate license. Please check your internet connection.';
      setLicenseError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  /**
   * Deactivate/remove the current license (via server-side proxy)
   */
  const deactivateLicense = useCallback(async () => {
    if (!license) return { success: true };

    try {
      // Try to deactivate on Lemon Squeezy
      await fetch(LICENSE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'deactivate',
          license_key: license.key,
          instance_id: license.instanceId,
        }),
      });
    } catch (error) {
      console.error('License deactivation error:', error);
      // Continue anyway - we'll remove locally
    }

    // Remove locally regardless of API result
    setLicense(null);
    setIsPro(false);
    localStorage.removeItem(LICENSE_STORAGE_KEY);

    return { success: true };
  }, [license]);

  // Load and validate license on mount
  useEffect(() => {
    const initLicense = async () => {
      const savedLicense = loadLicenseData();

      if (savedLicense?.key) {
        // Validate the saved license
        const result = await validateLicense(savedLicense.key);

        if (result.valid) {
          setLicense(savedLicense);
          setIsPro(true);
        } else {
          // License no longer valid - clear it
          localStorage.removeItem(LICENSE_STORAGE_KEY);
          setLicense(null);
          setIsPro(false);
        }
      } else {
        // Check for dev pro mode (for development/testing)
        const devPro = localStorage.getItem(DEV_PRO_STORAGE_KEY);
        if (devPro === 'true') {
          setIsPro(true);
        }
      }

      setIsLoading(false);
    };

    initLicense();
  }, [validateLicense]);

  // Persist usage data
  useEffect(() => {
    try {
      localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(usage));
    } catch (e) {
      console.error('Failed to save usage data:', e);
    }
  }, [usage]);

  // Show upgrade prompt
  const promptUpgrade = useCallback((reason) => {
    setUpgradeReason(reason);
    setShowUpgradeModal(true);
  }, []);

  /**
   * Check if an import is allowed (rate limited server-side)
   * @param {string} importType - Type of import ('csv' or '3d-model')
   * @returns {Promise<{allowed: boolean, remaining: number, isPro: boolean}>}
   */
  const checkImportAllowed = useCallback(async (importType) => {
    // Pro users always allowed
    if (isPro) {
      return { allowed: true, remaining: Infinity, isPro: true };
    }

    try {
      const response = await fetch(RATE_LIMIT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          licenseKey: license?.key || null,
          importType,
        }),
      });

      const data = await response.json();

      if (!data.allowed) {
        // Show upgrade prompt when rate limit reached
        promptUpgrade(`You've used your 3 free imports for today. Upgrade to Pro for unlimited imports.`);
      }

      return data;
    } catch (error) {
      console.error('Rate limit check failed:', error);
      // Fail open - allow import if rate limit check fails
      return { allowed: true, remaining: 3, isPro: false, error: 'Rate limit check failed' };
    }
  }, [isPro, license?.key, promptUpgrade]);

  // Legacy function - kept for backwards compatibility
  // @deprecated Use checkImportAllowed instead
  const canUseModelImport = useCallback(() => {
    return isPro;
  }, [isPro]);

  // Legacy function - no longer tracks locally
  // @deprecated Rate limiting is now server-side
  const recordModelImportUsed = useCallback(() => {
    // No-op: rate limiting is now server-side
  }, []);

  // Check if algorithm is available
  const canUseAlgorithm = useCallback((algorithmId) => {
    if (isPro) return true;
    return FREE_TIER_LIMITS.ALGORITHMS.includes(algorithmId);
  }, [isPro]);

  // Get list of available algorithms
  const getAvailableAlgorithms = useCallback(() => {
    if (isPro) return null;
    return FREE_TIER_LIMITS.ALGORITHMS;
  }, [isPro]);

  // Check if feature is available
  const hasFeature = useCallback((feature) => {
    if (isPro) return true;

    switch (feature) {
      case FEATURES.ALL_ALGORITHMS:
        return false;
      case FEATURES.MODEL_IMPORT:
        // Model import gating is now handled via server-side rate limiting
        return true;
      case FEATURES.CLEAN_EXPORT:
        return false;
      case FEATURES.PROJECT_SAVE:
        return false;
      default:
        return true;
    }
  }, [isPro]);

  // Dismiss upgrade modal
  const dismissUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeReason('');
  }, []);

  // Open license key modal
  const openLicenseModal = useCallback(() => {
    setShowLicenseModal(true);
  }, []);

  // Close license key modal
  const closeLicenseModal = useCallback(() => {
    setShowLicenseModal(false);
    setLicenseError(null);
  }, []);

  // For development/testing: toggle pro status (persisted)
  const toggleProStatus = useCallback(() => {
    setIsPro(prev => {
      const newValue = !prev;
      localStorage.setItem(DEV_PRO_STORAGE_KEY, JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // Reset usage (for testing)
  const resetUsage = useCallback(() => {
    setUsage({
      modelImportsUsed: 0,
    });
  }, []);

  const value = {
    // License state
    license,
    isPro,
    isLoading,
    licenseError,

    // License actions
    activateLicense,
    deactivateLicense,
    validateLicense,

    // License modal
    showLicenseModal,
    openLicenseModal,
    closeLicenseModal,

    // Usage tracking & rate limiting
    usage,
    checkImportAllowed,
    canUseModelImport,
    recordModelImportUsed,

    // Feature gating
    canUseAlgorithm,
    getAvailableAlgorithms,
    hasFeature,

    // Upgrade prompts
    showUpgradeModal,
    upgradeReason,
    promptUpgrade,
    dismissUpgradeModal,

    // Dev helpers
    toggleProStatus,
    resetUsage,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
